import React, { Component, Fragment } from "react";
import PropTypes from "prop-types";
import { Faq } from "routes";
import { idPropType, productPropType } from "components/propTypes";
import { displayRetailValueCopy } from "helpers/pricing-helper";
import {
  normalizePrice,
  percentOff,
  retailPriceFormatted as getRetailPriceFormatted,
  usdPriceIntoFloat,
} from "helpers/PricingHelper";
import { productIsHomeGood } from "helpers/homegood-helpers";
import Tooltip from "components/design-system/tooltip";

export default class AtomRetailPrice extends Component {
  static propTypes = {
    className: PropTypes.string,
    designerId: idPropType.isRequired,
    hideRetailValue: PropTypes.bool,
    isClearance: PropTypes.bool,
    priceClassName: PropTypes.string,
    product: productPropType,
    purchasePrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // 123.0 or "USD 123"
    purchasePriceDiscountFromRetailPrice: PropTypes.number,
    retailPrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // 123.0 or "USD 123"
    retailPriceFormatted: PropTypes.string,
    showPurchasePrice: PropTypes.bool,
    showPurchasePriceDiscount: PropTypes.bool,
    showPurchasePriceTooltip: PropTypes.bool,
    suppressRetailValueCopy: PropTypes.bool,
  };

  static defaultProps = {
    isSubscriptionLens: false,
  };

  state = {};

  renderDiscount() {
    const {
      purchasePrice: purchasePriceFormatted,
      showPurchasePriceDiscount,
      retailPrice,
      retailPriceFormatted = getRetailPriceFormatted(retailPrice),
      product,
      isClearance,
    } = this.props;

    if (!showPurchasePriceDiscount || !retailPriceFormatted) return;

    const retailPriceAsFloat = usdPriceIntoFloat(retailPriceFormatted);
    const purchasePrice = usdPriceIntoFloat(purchasePriceFormatted);
    const discount =
      product?.purchasePriceDiscountFromRetailPrice ||
      this.props.purchasePriceDiscountFromRetailPrice ||
      percentOff(retailPriceAsFloat, purchasePrice);

    // If there is no discount, we do not want to display the price
    if (discount <= 0) return;
    if (isClearance) {
      return <span className="purchase-price-discount">{` ${discount}%`}</span>;
    }
    return <span className="purchase-price-discount">{` (${discount}% off)`}</span>;
  }

  renderPurchaseTooltip() {
    const { showPurchasePriceTooltip, product } = this.props;
    let toolTipText;

    if (!showPurchasePriceTooltip) return;

    if (productIsHomeGood(product)) {
      toolTipText =
        "Home goods are not currently available for purchase prior to renting. To buy this item, please rent it first.";
    }

    return <Tooltip className="ml-1" tooltipContent={toolTipText} />;
  }

  renderRetailTooltip() {
    const { showPurchasePriceTooltip, isClearance } = this.props;
    const universalStyle = "universal-small--semibold universal-underline";

    if (!showPurchasePriceTooltip) return;

    const toolTipText = (
      <Fragment>
        This number represents the “Original Retail” or “Comparable Value” price. Items may be in the rental rotation
        for multiple seasons after these prices are set. For more info on these prices, see our{" "}
        <a className={universalStyle} href={Faq.purchasePricing} rel="noopener noreferrer" target="_blank">
          FAQs
        </a>
      </Fragment>
    );

    return (
      <Tooltip
        className="ml-1"
        position={isClearance ? "left" : "bottom"}
        tooltipContent={<div className="text-onyx w-40 sm:w-64 p-2">{toolTipText}</div>}
      />
    );
  }

  renderPurchasePrice() {
    const { purchasePrice, showPurchasePrice } = this.props;

    if (!purchasePrice || !showPurchasePrice) return;

    return (
      <span className="purchase-price pricing-divider">
        {`${normalizePrice(usdPriceIntoFloat(purchasePrice))} to buy`}

        {this.renderDiscount()}
        {this.renderPurchaseTooltip()}
      </span>
    );
  }

  renderClearancePricing() {
    const { purchasePrice, showPurchasePrice } = this.props;

    if (!purchasePrice || !showPurchasePrice) return;

    return (
      <div className="flex text-mutedRed">
        <div className="mr-2 semibold ">{`${normalizePrice(usdPriceIntoFloat(purchasePrice))}`}</div>
        <div className="mr-2 flex">
          <span className="pr-2 border-r border-offWhite">{this.renderDiscount()} Off (Final Sale)</span>
          <div className="text-darkerGray ml-2 flex items-center">{this.renderRetailPrice()}</div>
        </div>
      </div>
    );
  }

  renderRetailPrice = () => {
    let retailPriceText;
    const {
      designerId,
      priceClassName,
      retailPrice,
      retailPriceFormatted = getRetailPriceFormatted(retailPrice),
      suppressRetailValueCopy,
      hideRetailValue,
    } = this.props;

    if (hideRetailValue || !retailPriceFormatted) {
      return;
    }

    retailPriceText = retailPriceFormatted;
    if (!suppressRetailValueCopy) {
      retailPriceText += ` ${displayRetailValueCopy(designerId)}`;
    }

    return (
      <>
        <span className={priceClassName}>{retailPriceText}</span>
        {this.renderRetailTooltip()}
      </>
    );
  };

  render() {
    const { className, isClearance } = this.props;

    if (isClearance) {
      return <div className={`atom-retail-price  ${className || ""}`}>{this.renderClearancePricing()}</div>;
    }

    return (
      <span className={`atom-retail-price text-darkGray ${className || ""}`}>
        {this.renderRetailPrice()}
        {this.renderPurchasePrice()}
      </span>
    );
  }
}
